/**
 * Narrowing a result set by curated keywords on the client
 * (docs/concepts/concept_curated_keywords.md §10.1, §10.4).
 *
 * The search returns every result (in rank order) with its keyword IDs.
 * Selecting keywords keeps the results carrying all of them; nothing is
 * searched again, so counts are exact and the ranking is preserved.
 */

import type { KeywordNavigation } from '../types/search';

export interface TopicOption {
  id: string;
  label: string;
  /** Results that remain when this keyword is (or stays) selected. */
  count: number;
  selected: boolean;
}

/** IDs of the results carrying all selected keywords, in rank order. */
export function narrowedDocumentIds(navigation: KeywordNavigation, selected: readonly string[]): string[] {
  return navigation.entries
    .filter((entry) => selected.every((id) => entry.keyword_ids.includes(id)))
    .map((entry) => entry.document_id);
}

/**
 * The drawer's list: selected keywords first (always, with their count), then
 * the keywords that narrow the current set, by count and label, capped at
 * `max_n`. Keywords on fewer than `min_count` results or on more than
 * `max_share` of them are hidden, because they hardly narrow anything.
 */
export function topicOptions(
  navigation: KeywordNavigation,
  selected: readonly string[],
  locale?: string
): TopicOption[] {
  const narrowed = navigation.entries.filter((entry) =>
    selected.every((id) => entry.keyword_ids.includes(id))
  );
  const counts = new Map<string, number>();
  for (const entry of narrowed) {
    for (const id of entry.keyword_ids) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const label = (id: string) => navigation.labels[id] ?? id;

  const chosen = selected.map((id) => ({ id, label: label(id), count: counts.get(id) ?? 0, selected: true }));
  const others = [...counts]
    .filter(([id, count]) =>
      !selected.includes(id) &&
      count >= navigation.min_count &&
      count / narrowed.length <= navigation.max_share
    )
    .map(([id, count]) => ({ id, label: label(id), count, selected: false }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, locale));
  return [...chosen, ...others.slice(0, Math.max(0, navigation.max_n - chosen.length))];
}

/** The selection with `id` added or removed. */
export function toggleKeyword(selected: readonly string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id];
}
